import { AgentClient } from 'agents/client'
import { hash, isEqual } from 'ohash'
import { useIDBKeyval } from '@vueuse/integrations/useIDBKeyval'
import { isTokenFresh, tokenState, useAuthToken } from '~/queries/auth/token'
import type { AgentConnectionState } from '~/types/agents'

type AgentOptions = {
  agent: MaybeRefOrGetter<string>
  name: MaybeRefOrGetter<string>
  host: string
  baseURL?: string
}

export function useAgent<T extends Record<string, unknown>>({
  agent,
  name,
  host,
  baseURL,
}: AgentOptions) {
  const AGENT = toValue(agent)
  const NAME = toValue(name)

  if (!AGENT || !NAME || !host) {
    throw new Error('Agent, name, and host are required')
  }

  const {
    data: state,
    set: setLocalState,
    isFinished: stateReady,
  } = useIDBKeyval<T>(`@rialtic/agents/${AGENT}/${NAME}`, {} as T, {
    deep: true,
    shallow: false,
  })

  const serverStateLoaded = shallowRef(false)
  const connectionState = ref<AgentConnectionState>('disconnected')
  const connectionError = ref<string | null>(null)

  /* The server never echoes an accepted client update back to its sender, so a
     local snapshot can't tell which edits it kept. After a refusal we reconnect,
     which makes the server send its current state, and only then report the
     refusal, so drafts reset to what the server actually holds.
     If the reconnect fails, the existing connection-error state shows instead,
     and the refusal waits for the server state a later reconnect brings. */
  const pendingRefusal = shallowRef<{ error: string }>()

  const stateUpdatedHook = createEventHook<{
    state: T
    source: 'client' | 'server'
    user?: any
  }>()
  const stateErrorHook = createEventHook<string>()
  const reportPendingRefusal = () => {
    const refusal = pendingRefusal.value
    if (!refusal) return
    pendingRefusal.value = undefined
    stateErrorHook.trigger(refusal.error)
  }

  const toPlain = <U>(value: U): U => {
    const raw = toRaw(value)

    try {
      return structuredClone(raw)
    } catch {
      return JSON.parse(
        JSON.stringify(raw, (_key, val) =>
          typeof val === 'function' || typeof val === 'symbol'
            ? undefined
            : val,
        ),
      ) as U
    }
  }

  const persistState = (agentState: T) => {
    if (isEqual(agentState, state.value)) {
      return Promise.resolve()
    }
    return setLocalState(toPlain(agentState))
  }

  const { data: authToken, refetch: refetchAuthToken } = useAuthToken()
  const { $groundcover } = useNuxtApp()
  /* Lets the API log which admin build is connecting, so a stale cached
   * bundle shows up as an old build id instead of a mystery. */
  const buildId = useRuntimeConfig().app.buildId
  const reportConnection = (message: string) =>
    $groundcover?.captureException(
      new Error(
        `${message} [token ${tokenState(authToken.value)}, build ${buildId}]`,
      ),
    )
  const connection = new AgentClient({
    agent: AGENT,
    name: NAME,
    host,
    query: async () => {
      if (isTokenFresh(authToken.value)) {
        return { token: authToken.value, v: buildId }
      }
      connectionState.value = 'connecting'
      connectionError.value = null
      try {
        const { data: token } = await refetchAuthToken()
        if (!isTokenFresh(token)) {
          throw new Error(`No usable auth token (${tokenState(token)})`)
        }
        return { token, v: buildId }
      } catch (error) {
        connectionState.value = 'error'
        connectionError.value =
          error instanceof Error ? error.message : 'Failed to connect to agent'
        reportConnection(connectionError.value)
        throw error
      }
    },
    onStateUpdate: async (agentState: T, source) => {
      /* Read before any await: only a server state that arrives after the
         refusal reflects the resync. */
      const refusal = source === 'server' ? pendingRefusal.value : undefined
      stateUpdatedHook.trigger({
        state: agentState,
        source,
      })

      if (!serverStateLoaded.value) {
        if (source === 'server') {
          await persistState(agentState)
          serverStateLoaded.value = true
          return
        }
        // client update before server state initialized
        // return early to avoid persisting stale state
        return
      }

      await persistState(agentState)

      if (refusal === undefined || pendingRefusal.value !== refusal) return
      reportPendingRefusal()
    },
    onStateUpdateError: (error: string) => {
      $groundcover?.captureException(
        new Error(`Agent state update refused: ${error}`),
      )
      pendingRefusal.value = { error }
      connection.reconnect()
    },
  })

  // Track connection state changes
  const updateConnectionState = () => {
    if (connection.CLOSED) {
      connectionState.value = 'disconnected'
    } else {
      connectionState.value = 'connected'
    }
  }

  // Listen for connection events
  connection.addEventListener('open', () => {
    connectionState.value = 'connected'
    connectionError.value = null
  })

  connection.addEventListener('close', (event) => {
    connectionState.value = 'disconnected'
    /* 1000/1001 are a normal close or navigation; anything else is worth a look. */
    if (event.code !== 1000 && event.code !== 1001) {
      reportConnection(
        `Agent WebSocket closed (code ${event.code}, reason "${event.reason}")`,
      )
    }
  })

  connection.addEventListener('error', (event) => {
    connectionState.value = 'error'
    connectionError.value =
      event instanceof Error ? event.message : 'Connection error'
    reportConnection(`Agent WebSocket error: ${connectionError.value}`)
  })

  const messageHook = createEventHook<{
    type: string
    data?: any
    user?: any
  }>()
  connection.addEventListener('message', (event) => {
    if (typeof event.data === 'string') {
      try {
        const data = JSON.parse(event.data)
        messageHook.trigger(data)
      } catch (error) {
        $groundcover?.captureException(error as Error)
      }
    }
  })

  const reconnectInterval = ref<NodeJS.Timeout>()

  onBeforeMount(() => {
    if (connection.CLOSED) {
      connectionState.value = 'connecting'
      connection.reconnect()
    } else {
      updateConnectionState()

      reconnectInterval.value = setInterval(
        () => {
          if (
            Boolean(connectionError.value) ||
            connectionState.value === 'disconnected'
          ) {
            connection.reconnect()
          }
        },
        1000 * 60 * 5,
      )
    }
  })
  onBeforeUnmount(() => {
    clearInterval(reconnectInterval.value)
    connection.close()
  })

  const { user } = useUserSession()
  const sendMessage = async (message: { type: string; data: any }) => {
    if (!message.type || !message.data) {
      return
    }
    connection.send(
      JSON.stringify({
        ...message,
        user: { ...user.value, id: hash(user.value?.sub) },
      }),
    )
  }

  const agentFetch = $fetch.create({
    baseURL: `${baseURL}/${AGENT}/${NAME}`,
  })

  const setState = (state: T) => {
    connection.setState(state)
  }

  const updateState = (updates: Partial<T>) => {
    const currentState = toPlain(state.value)
    const nextUpdates = toPlain(updates)

    setState({ ...(currentState as T), ...(nextUpdates as Partial<T>) })
  }

  return {
    agentFetch,
    connection,
    onMessage: messageHook.on,
    onStateUpdate: stateUpdatedHook.on,
    onStateUpdateError: stateErrorHook.on,
    serverStateLoaded,
    sendMessage,
    state,
    setState,
    stateReady,
    updateState,
    reconnect: () => connection.reconnect(),
    connectionState: readonly(connectionState),
    connectionError: readonly(connectionError),
  }
}
